import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import Card from '../../components/common/Card/Card';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Loading from '../../components/common/Loading/Loading';
import Logo from '../../components/common/Logo/Logo';
import { appOnboardingText } from '../../components/common/appUi';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import type { EquipmentItem } from '../../constants/equipmentCategory';
import {
  PublicStudentContextService,
  PublicStudentTokenAlreadyUsedError,
  StudentEquipmentService,
} from '../../services';
import {
  clearEquipmentSelectionDraft,
  readEquipmentSelectionDraft,
  writeEquipmentSelectionDraft,
} from '../../utils/equipmentSelectionDraft';
import { PUBLIC_LINK_GENERIC_ERROR } from '../../utils/publicLinkErrors';
import { STUDENT_TOKEN_QUERY_PARAM } from '../../utils/studentPublicLinks';
import trainingStyles from '../Training/components/TrainingBlockEditor/TrainingBlockEditor.module.css';
import fupStyles from '../Fup/Fup.module.css';
import styles from '../PhotoGuidelines/PhotoGuidelinesPage.module.css';
import equipmentStyles from './EquipmentSelectionPage.module.css';

const formatGreetingName = (fullName: string): string => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return 'aluna';
  }

  const [firstName] = trimmed.split(/\s+/);
  const normalized = firstName.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const AlreadyReceivedView: React.FC<{ studentName: string | null }> = ({ studentName }) => {
  const firstName = studentName ? formatGreetingName(studentName) : null;

  return (
    <div className={equipmentStyles.headerBlock}>
      <p className={equipmentStyles.greetingLead}>
        {firstName ? `Olá, ${firstName}` : 'Olá'}
      </p>
      <DecoratedHeader
        className={equipmentStyles.headerTitle}
        title="Já recebemos seus equipamentos"
      />
      <p className={`${appOnboardingText.intro} ${equipmentStyles.intro}`}>
        Obrigada por informar. Está tudo certo por aqui.
      </p>
    </div>
  );
};

/**
 * Public page for selecting available gym equipment (one-shot token).
 * Flat catalog grid with search and a single send action.
 */
const EquipmentSelectionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const studentToken = searchParams.get(STUDENT_TOKEN_QUERY_PARAM)?.trim() || '';
  const [studentId, setStudentId] = useState<number | null>(null);
  const [studentName, setStudentName] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(Boolean(studentToken));
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [equipments, setEquipments] = useState<EquipmentItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [draftReady, setDraftReady] = useState(false);
  const skipNextDraftWriteRef = useRef(true);

  const catalogIdSet = useMemo(
    () => new Set(equipments.map((item) => item.id)),
    [equipments],
  );

  const orderedEquipments = useMemo(
    () => StudentEquipmentService.groupByCategory(equipments).flatMap((group) => group.items),
    [equipments],
  );

  const visibleEquipments = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('pt-BR');
    if (!query) {
      return orderedEquipments;
    }

    return orderedEquipments.filter((item) => (
      item.name.toLocaleLowerCase('pt-BR').includes(query)
    ));
  }, [orderedEquipments, searchQuery]);

  useEffect(() => {
    if (!studentToken) {
      setStudentId(null);
      setResolveError(PUBLIC_LINK_GENERIC_ERROR);
      setIsResolving(false);
      setDraftReady(false);
      return;
    }

    let isMounted = true;

    const load = async () => {
      setIsResolving(true);
      setResolveError(null);
      setAlreadySubmitted(false);
      setSubmitted(false);
      setDraftReady(false);
      skipNextDraftWriteRef.current = true;
      setSearchQuery('');

      try {
        const context = await PublicStudentContextService.resolve(studentToken);
        if (!isMounted) {
          return;
        }

        setStudentId(context.student.id);
        setStudentName(context.student.full_name);
        setIsResolving(false);
        setIsLoadingCatalog(true);

        const [catalog, selected] = await Promise.all([
          StudentEquipmentService.listCatalog({
            studentId: context.student.id,
            studentToken,
          }),
          StudentEquipmentService.getSelectedIds({
            studentId: context.student.id,
            studentToken,
          }),
        ]);

        if (!isMounted) {
          return;
        }

        const validIds = new Set(catalog.map((item) => item.id));
        const draft = readEquipmentSelectionDraft(studentToken);
        const restoredIds = draft?.ids.filter((id) => validIds.has(id)) ?? [];
        const hasDraft = draft !== null;
        const initialIds = hasDraft ? restoredIds : selected.filter((id) => validIds.has(id));

        setEquipments(catalog);
        setSelectedIds(new Set(initialIds));
        setDraftReady(true);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error instanceof PublicStudentTokenAlreadyUsedError) {
          clearEquipmentSelectionDraft(studentToken);
          setStudentId(null);
          setAlreadySubmitted(true);
          setResolveError(null);
          return;
        }

        setStudentId(null);
        setAlreadySubmitted(false);
        setDraftReady(false);
        setResolveError(
          error instanceof Error ? error.message : 'Não foi possível carregar os equipamentos.',
        );
      } finally {
        if (isMounted) {
          setIsResolving(false);
          setIsLoadingCatalog(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [studentToken]);

  useEffect(() => {
    if (!draftReady || !studentToken || alreadySubmitted || submitted) {
      return;
    }

    if (skipNextDraftWriteRef.current) {
      skipNextDraftWriteRef.current = false;
      return;
    }

    const ids = Array.from(selectedIds).filter((id) => catalogIdSet.has(id));
    writeEquipmentSelectionDraft(studentToken, {
      ids,
      step: 0,
    });
  }, [
    alreadySubmitted,
    catalogIdSet,
    draftReady,
    selectedIds,
    studentToken,
    submitted,
  ]);

  const selectedEquipments = useMemo(
    () => orderedEquipments.filter((item) => selectedIds.has(item.id)),
    [orderedEquipments, selectedIds],
  );

  const toggleEquipment = (equipmentId: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(equipmentId)) {
        next.delete(equipmentId);
      } else {
        next.add(equipmentId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!studentId || !studentToken || isSaving) {
      return;
    }

    setIsSaving(true);
    setFeedback(null);

    try {
      await StudentEquipmentService.syncSelection({
        studentId,
        studentToken,
        equipmentIds: Array.from(selectedIds),
      });
      clearEquipmentSelectionDraft(studentToken);
      setShowConfirmModal(false);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (
        error instanceof Error
        && (/já recebemos/i.test(error.message) || (error as Error & { code?: string }).code === 'TOKEN_ALREADY_USED')
      ) {
        clearEquipmentSelectionDraft(studentToken);
        setAlreadySubmitted(true);
        setShowConfirmModal(false);
        return;
      }

      setFeedback({
        message: error instanceof Error ? error.message : 'Não foi possível salvar os equipamentos.',
        variant: 'error',
      });
      setShowConfirmModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (isResolving || isLoadingCatalog) {
    return <Loading fullscreen />;
  }

  if (alreadySubmitted || submitted) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.header}>
            <Logo />
          </div>
          <AlreadyReceivedView studentName={studentName} />
        </div>
      </div>
    );
  }

  if (resolveError || !studentId) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.header}>
            <Logo />
          </div>
          <AlertBanner
            message={resolveError || PUBLIC_LINK_GENERIC_ERROR}
            variant="error"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.shell} ${equipmentStyles.wizardShell}`}>
        <div className={styles.header}>
          <Logo />
        </div>

        <div className={equipmentStyles.headerBlock}>
          <DecoratedHeader
            className={equipmentStyles.headerTitle}
            title="Equipamentos"
          />
          <p className={`${appOnboardingText.intro} ${equipmentStyles.intro}`}>
            Para dar continuidade, selecione os equipamentos disponíveis no seu local de treino. Se você não tiver acesso a algum item, deixe ele desmarcado.
          </p>
        </div>

        {feedback && (
          <AlertBanner message={feedback.message} variant={feedback.variant} />
        )}

        <div className={equipmentStyles.searchBar}>
          <SearchFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Pesquisar equipamento"
            searchWrapperClassName={equipmentStyles.searchBarWrapper}
            inputClassName={equipmentStyles.searchInput}
          />
        </div>

        <div className={equipmentStyles.items}>
          {visibleEquipments.map((item) => {
            const isSelected = selectedIds.has(item.id);

            return (
              <Card
                key={item.id}
                className={[
                  trainingStyles.exerciseCard,
                  trainingStyles.exerciseCardCompact,
                  equipmentStyles.equipmentCard,
                  isSelected ? equipmentStyles.equipmentCardSelected : '',
                ].filter(Boolean).join(' ')}
              >
                <button
                  type="button"
                  className={`${trainingStyles.exerciseButton} ${equipmentStyles.equipmentButton}`}
                  onClick={() => toggleEquipment(item.id)}
                  disabled={isSaving}
                  aria-pressed={isSelected}
                  aria-label={item.name}
                >
                  <div
                    className={[
                      trainingStyles.thumbnailFrame,
                      trainingStyles.thumbnailFrameCompact,
                      equipmentStyles.thumbnailFrame,
                    ].join(' ')}
                  >
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className={trainingStyles.thumbnail}
                        loading="lazy"
                      />
                    ) : (
                      <span className={`material-symbols-outlined ${equipmentStyles.imageFallback}`}>
                        fitness_center
                      </span>
                    )}
                    <div className={trainingStyles.thumbnailOverlay}>
                      <span className={trainingStyles.exerciseName} title={item.name}>
                        {item.name}
                      </span>
                    </div>
                    {isSelected && (
                      <span className={equipmentStyles.selectedCheck} aria-hidden="true">
                        <span className={`material-symbols-outlined ${equipmentStyles.selectedCheckIcon}`}>
                          check
                        </span>
                      </span>
                    )}
                  </div>
                </button>
              </Card>
            );
          })}
        </div>

        {visibleEquipments.length === 0 && (
          <p className={equipmentStyles.emptySearch}>
            Nenhum equipamento encontrado para essa busca.
          </p>
        )}
      </div>

      {equipments.length > 0 && (
        <div className={fupStyles.footerActions}>
          <Button
            type="button"
            variant="primary"
            size="app"
            fullWidth
            disabled={isSaving}
            onClick={() => setShowConfirmModal(true)}
          >
            {isSaving ? 'Enviando...' : 'Enviar equipamentos'}
          </Button>
        </div>
      )}

      <ConfirmModal
        open={showConfirmModal}
        title="Confirmar equipamentos"
        message={
          selectedEquipments.length > 0
            ? 'Tem certeza que deseja enviar estes equipamentos?'
            : 'Nenhum equipamento selecionado. Deseja enviar mesmo assim?'
        }
        confirmLabel="Enviar"
        cancelLabel="Voltar"
        variant="info"
        icon="fitness_center"
        loading={isSaving}
        onConfirm={() => {
          void handleSave();
        }}
        onCancel={() => setShowConfirmModal(false)}
      >
        {selectedEquipments.length > 0 && (
          <ul className={equipmentStyles.selectedList}>
            {selectedEquipments.map((item) => (
              <li key={item.id} className={equipmentStyles.selectedListItem}>
                {item.name}
              </li>
            ))}
          </ul>
        )}
      </ConfirmModal>
    </div>
  );
};

export default EquipmentSelectionPage;
